<template>
  <div class="tokens-container">
    <h1>{{ $t('developer.title') }}</h1>
    <p class="description">{{ $t('developer.description') }}</p>

    <div class="tokens-content">
      <div class="tokens-header">
        <h2 class="header-2">{{ $t('developer.yourKeys') }}</h2>
        <PrimaryBtn size="sm" @click="showCreateModal = true" :disabled="isLoading">
          <span class="btn-inner"><BaseIcon iconName="plus" :width="16" :height="16" /> {{ $t('developer.newKey') }}</span>
        </PrimaryBtn>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="empty-state">
        <p>{{ $t('developer.loading') }}</p>
      </div>

      <!-- Empty state -->
      <div v-else-if="keys.length === 0" class="empty-state">
        <BaseIcon iconName="key" :width="48" :height="48" color="rgba(255,255,255,0.2)" />
        <p>{{ $t('developer.empty') }}</p>
        <p class="empty-hint">{{ $t('developer.emptyHint') }}</p>
      </div>

      <!-- Key table -->
      <div v-else class="key-table">
        <div class="key-table-header">
          <span class="col-name">{{ $t('developer.name') }}</span>
          <span class="col-key">{{ $t('developer.key') }}</span>
          <span class="col-created">{{ $t('developer.created') }}</span>
          <span class="col-usage">{{ $t('developer.usage') }}</span>
          <span class="col-revoke"></span>
        </div>
        <div v-for="key in keys" :key="key.id" class="key-row">
          <span class="col-name">{{ key.name }}</span>
          <span class="col-key"><code>{{ key.token_prefix }}...</code></span>
          <span class="col-created">{{ formatDate(key.created_at) }}</span>
          <span class="col-usage">{{ key.usage_count }}</span>
          <span class="col-revoke">
            <button class="revoke-btn" @click="confirmRevoke(key)" :disabled="isRevoking">
              <BaseIcon iconName="bin" :width="18" :height="18" color="rgba(255,255,255,0.4)" />
            </button>
          </span>
        </div>
      </div>
    </div>

    <!-- How to use -->
    <div class="how-to-use">
      <h2 class="header-2">{{ $t('developer.howTo') }}</h2>
      <ol class="steps-list">
        <li>{{ $t('developer.step1') }}</li>
        <li>{{ $t('developer.step2') }}</li>
        <li>{{ $t('developer.step3') }}</li>
      </ol>
    </div>

    <!-- Create Key Modal -->
    <BaseModal
      :show="showCreateModal"
      :title="$t('developer.createTitle')"
      :confirmText="$t('common.create')"
      :cancelText="$t('common.cancel')"
      @confirm="createKey"
      @cancel="cancelCreate"
    >
      <BaseInput
        v-model="newKeyName"
        :placeholder="$t('developer.keyPlaceholder')"
        icon="key"
      />
    </BaseModal>

    <!-- Key Created Success Modal -->
    <BaseModal
      :show="showSuccessModal"
      :title="$t('developer.keyCreated')"
      :confirmText="$t('common.done')"
      :cancelText="$t('developer.copy')"
      @confirm="closeSuccessModal"
      @cancel="copyKey"
    >
      <p class="success-warning">{{ $t('developer.copyWarning') }}</p>
      <div class="key-display">
        <code class="key-value">{{ createdKey }}</code>
        <button class="copy-btn" @click="copyKey">
          <BaseIcon :iconName="copyIcon" :width="18" :height="18" />
        </button>
      </div>
      <p v-if="pluginAuthSent" class="plugin-sent-msg">{{ $t('developer.tokenSent') }}</p>
    </BaseModal>

    <!-- Revoke Confirmation Modal -->
    <BaseModal
      :show="showRevokeModal"
      :title="$t('developer.revokeTitle')"
      :confirmText="$t('developer.revoke')"
      :cancelText="$t('common.cancel')"
      :danger="true"
      @confirm="revokeKey"
      @cancel="showRevokeModal = false"
    >
      {{ $t('developer.revokeQuestion', { name: keyToRevoke?.name }) }}
    </BaseModal>

    <NotificationToast
      :show="showNotification"
      :message="notificationMessage"
      :is-error="isErrorNotification"
      @close="showNotification = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { userAPI } from "@/services/api";
import BaseIcon from "@/components/BaseComponents/BaseIcon.vue";
import BaseModal from "@/components/BaseComponents/BaseModal.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import PrimaryBtn from "@/components/BaseComponents/PrimaryBtn.vue";
import NotificationToast from "@/components/BaseComponents/NotificationToast.vue";
import { useI18n } from 'vue-i18n';

const store = useStore();
const route = useRoute();
const { locale, t } = useI18n();
const sessionId = computed(() => store.getters.getSessionId);

const keys = ref([]);
const isLoading = ref(false);
const isCreating = ref(false);
const isRevoking = ref(false);

const showCreateModal = ref(false);
const newKeyName = ref("");

const showSuccessModal = ref(false);
const createdKey = ref("");

const showRevokeModal = ref(false);
const keyToRevoke = ref(null);

const copyIcon = ref("copy");

const showNotification = ref(false);
const notificationMessage = ref("");
const isErrorNotification = ref(false);

// Plugin auth params
const pluginAuthPort = ref(null);
const pluginAuthSent = ref(false);

function notify(message, isError = false) {
  notificationMessage.value = message;
  isErrorNotification.value = isError;
  showNotification.value = true;
}

async function loadKeys() {
  isLoading.value = true;
  try {
    keys.value = await userAPI.getApiTokens(sessionId.value);
  } catch (e) {
    notify(t('developer.failedLoad'), true);
  } finally {
    isLoading.value = false;
  }
}

async function createKey() {
  if (isCreating.value || !newKeyName.value.trim()) return;
  isCreating.value = true;
  try {
    const result = await userAPI.createApiToken(sessionId.value, newKeyName.value.trim());
    createdKey.value = result.token;
    showCreateModal.value = false;
    showSuccessModal.value = true;
    newKeyName.value = "";
    pluginAuthSent.value = false;

    // Reload the keys list
    await loadKeys();

    // If plugin auth mode, auto-send token to plugin
    if (pluginAuthPort.value) {
      await sendTokenToPlugin(result.token);
    }
  } catch (e) {
    notify(t('developer.failedCreate'), true);
  } finally {
    isCreating.value = false;
  }
}

function cancelCreate() {
  showCreateModal.value = false;
  newKeyName.value = "";
}

function confirmRevoke(key) {
  keyToRevoke.value = key;
  showRevokeModal.value = true;
}

async function revokeKey() {
  isRevoking.value = true;
  try {
    await userAPI.revokeApiToken(sessionId.value, keyToRevoke.value.id);
    showRevokeModal.value = false;
    notify(t('developer.revoked'));
    await loadKeys();
  } catch (e) {
    notify(t('developer.failedRevoke'), true);
  } finally {
    isRevoking.value = false;
  }
}

function closeSuccessModal() {
  showSuccessModal.value = false;
}

async function copyKey() {
  try {
    await navigator.clipboard.writeText(createdKey.value);
    copyIcon.value = "check";
    setTimeout(() => {
      copyIcon.value = "copy";
    }, 2000);
  } catch {
    notify(t('developer.failedCopy'), true);
  }
}

async function sendTokenToPlugin(token) {
  try {
    await fetch(`http://localhost:${pluginAuthPort.value}/callback?token=${encodeURIComponent(token)}`, {
      mode: 'no-cors',
    });
    pluginAuthSent.value = true;
  } catch {
    // Plugin server may not be running — that's OK, user can still copy manually
  }
}

function formatDate(dateStr) {
  if (!dateStr) return t('profile.notAvailable');
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale.value, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

onMounted(() => {
  loadKeys();

  // Check for plugin auth params
  if (route.query.plugin_auth === 'true' && route.query.port) {
    pluginAuthPort.value = route.query.port;
    // Auto-open create modal with default name
    newKeyName.value = "Blender Plugin";
    showCreateModal.value = true;
  }
});
</script>

<style scoped>
.tokens-container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  flex: 1 0 auto;
  min-height: 100%;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

h1 {
  margin: 1em 0;
  font: inherit;
}

.description {
  color: var(--color-white-60);
  margin: 0 0 var(--spacing-2xl);
}

.tokens-content {
  width: 100%;
  text-align: left;
}

.tokens-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.tokens-header .header-2 {
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.tokens-header :deep(.btn-primary) {
  width: auto;
  min-width: 108px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
}

.btn-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font: inherit;
  line-height: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-4xl) 0;
  color: var(--color-white-40);
}

.empty-hint {
  font-size: var(--font-size-caption);
  margin-top: var(--spacing-xs);
}

.key-table {
  width: 100%;
}

.key-table-header {
  display: flex;
  align-items: center;
  padding: 0.4rem var(--padding-md);
  font-size: var(--font-size-caption);
  color: var(--color-white-40);
  font-weight: var(--font-weight-medium);
  border-bottom: 1px solid var(--color-white-10);
  margin-bottom: var(--spacing-xs);
}

.key-row {
  display: flex;
  align-items: center;
  padding: var(--padding-md);
  border-radius: var(--border-radius-md);
  background: var(--color-white-5);
  transition: background var(--transition-fast);
  margin-bottom: var(--spacing-xs);
}

.key-row:hover {
  background: var(--color-white-10);
}

.col-name {
  flex: 2;
  font-weight: var(--font-weight-medium);
  color: var(--color-white-90);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-key {
  flex: 2;
  color: var(--color-white-60);
  font-size: var(--font-size-caption);
  min-width: 0;
}

.col-key code {
  font-size: inherit;
}

.col-created {
  flex: 2;
  color: var(--color-white-40);
  font-size: var(--font-size-caption);
}

.col-usage {
  flex: 1;
  color: var(--color-white-40);
  font-size: var(--font-size-caption);
  text-align: center;
}

.col-revoke {
  flex-shrink: 0;
  width: 40px;
  display: flex;
  justify-content: center;
}

.revoke-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--border-radius-xs);
  transition: background var(--transition-fast);
}

.revoke-btn:hover {
  background: var(--color-cancel-10);
}

.how-to-use {
  width: 100%;
  text-align: left;
  margin-top: 4rem;
  padding-top: var(--spacing-3xl);
  border-top: 1px solid var(--color-white-10);
}

.how-to-use .header-2 {
  margin: 0 0 var(--spacing-lg);
}

.steps-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
  counter-reset: how-to-steps;
  color: var(--color-white-60);
  line-height: 1.8;
}

.steps-list li {
  display: grid;
  grid-template-columns: 20px 1fr;
  align-items: start;
  column-gap: var(--spacing-sm);
}

.steps-list li::before {
  counter-increment: how-to-steps;
  content: counter(how-to-steps) ".";
  color: var(--color-white-60);
}

.success-warning {
  color: var(--color-warning, #f0b429);
  font-size: var(--font-size-caption);
  margin: 0 0 var(--spacing-lg);
}

.key-display {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: var(--color-white-5);
  border-radius: var(--border-radius-md);
  padding: var(--padding-md);
}

.key-value {
  flex: 1;
  word-break: break-all;
  font-size: var(--font-size-caption);
  color: var(--color-white-90);
}

.copy-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--border-radius-xs);
  transition: background var(--transition-fast);
  flex-shrink: 0;
}

.copy-btn:hover {
  background: var(--color-white-10);
}

.plugin-sent-msg {
  color: var(--color-success, #4caf50);
  font-size: var(--font-size-caption);
  margin-top: var(--spacing-md);
}

@media (max-width: 850px) {
  .tokens-container {
    min-height: auto;
  }

  .key-table-header {
    display: none;
  }

  .key-row {
    flex-wrap: wrap;
    gap: var(--spacing-xs);
  }

  .col-name {
    flex: 1 1 100%;
  }

  .col-key {
    flex: 1;
  }

  .col-created,
  .col-usage {
    display: none;
  }
}
</style>
